import { Button } from '@televet/kibble-ui/Button';
import { Text } from '@televet/kibble-ui/Text';
import { Flex } from '@televet/kibble-ui/chakra';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PageContainer } from '@/components/PageContainer';
import { ClinicList } from './-components/ClinicList';
import { PetIdJump } from './-components/PetIdJump';
import { clinicListSearchSchema, legacyClinicTarget } from './-lib/listing';

const RouteComponent = () => {
  const navigate = Route.useNavigate();

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Breadcrumbs items={[{ label: 'Pet Conditions' }]} />

        {/* Three separate blocks, so the pet jump's Open button never reads as part of Browse concepts. */}
        <Flex align="stretch" gap={6} wrap="wrap">
          <Flex direction="column" gap={1} flex="2" minW="320px">
            <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
              Pet Conditions
            </Text>
            <Text variant="subtle" fontSize="sm">
              Read-only. Clinics with condition evidence. Tier columns count pets with at least one
              condition at that tier. Open a clinic to see its pets.
            </Text>
          </Flex>

          <Flex
            direction="column"
            gap={2}
            minW="280px"
            pl={6}
            borderLeft="1px solid"
            borderColor="border.default"
          >
            <Flex direction="column" gap={1}>
              <Text fontSize="sm" fontWeight="semibold">
                Jump to a pet
              </Text>
              <Text variant="subtle" fontSize="xs">
                Already have a pet ID from a query or a log? Open its evidence directly.
              </Text>
            </Flex>
            <PetIdJump />
          </Flex>

          <Flex
            direction="column"
            gap={2}
            minW="240px"
            maxW="300px"
            pl={6}
            borderLeft="1px solid"
            borderColor="border.default"
          >
            <Flex direction="column" gap={1}>
              <Text fontSize="sm" fontWeight="semibold">
                Condition concepts
              </Text>
              <Text variant="subtle" fontSize="xs">
                Every installed condition, its rules, and how much evidence it has produced across
                all clinics.
              </Text>
            </Flex>
            <Flex>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate({ to: '/pet-conditions/concepts' })}
                data-testid="pet-conditions-concepts-link"
              >
                Browse concepts
              </Button>
            </Flex>
          </Flex>
        </Flex>

        <ClinicList />
      </Flex>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_authenticated/pet-conditions/')({
  component: RouteComponent,
  validateSearch: clinicListSearchSchema,
  beforeLoad: ({ search }) => {
    const target = legacyClinicTarget(search);
    if (target) {
      throw redirect({
        to: '/pet-conditions/clinics/$clinicId',
        params: { clinicId: target.clinicId },
        search: { page: target.page },
        replace: true,
      });
    }
  },
});
